import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Input, Select } from 'antd';
import { Button } from '@cropler/ui-kit';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

import { useAddWorkspaceMutation } from '../../store/api/workspaces';
import { useAddWorkspaceMembersMutation } from '../../store/api/memberships';
import { authApi } from '../../store/api/auth';
import { userApi } from '../../store/api/user';

import styles from './styles.module.css';

import { ProgressSteps } from './ProgressSteps.jsx';
import { BreakpointMobile, BreakpointMobileUp } from '../../constants/ui';
import { useViewport } from '../../hooks';
import { AddWorkspaceModal, Workspace } from '../../constants/keys';
import { getRoles } from '../../utils/ui';

const { Option } = Select;

export const Organization = ({ t, goBack, setWorkspaceId, showOnlyCreateForm, setActiveModal }) => {
  const Roles = getRoles(t);
  const dispatch = useDispatch();
  const { widthViewport } = useViewport();

  const [step, setStep] = useState(1);

  const [form] = Form.useForm();

  const [addWorkspace] = useAddWorkspaceMutation();

  const [addMembers] = useAddWorkspaceMembersMutation();

  const name = Form.useWatch('name', form);
  const members = Form.useWatch('members', form);

  const disabled = step === 1 && !name;
  const [submittable, setSubmittable] = useState(false);

  const onBack = () => {
    if (step === 1) {
      goBack();
    } else {
      setStep(step - 1);
    }
  };

  const values = Form.useWatch([], form);
  useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      },
    );
  }, [form, submittable, values]);

  const onNext = () => {
    if (step === 1) {
      setStep(2);
    }

    if (step === 2) {
      const name = form.getFieldValue('name');
      const members = form.getFieldValue('members');

      if (name) {
        addWorkspace({ name })
          .unwrap()
          .then(({ id }) => {
            if (id) {
              if (!showOnlyCreateForm) {
                setWorkspaceId(id);
                localStorage.setItem(Workspace, JSON.stringify(id));
              }

              if (members && members[0].email) {
                addMembers({ id, members })
                  .unwrap()
                  .catch((errors) => {
                    console.log('Some emails are not valid', errors);
                  });
              }

              dispatch(authApi.util.resetApiState());
              dispatch(
                userApi.endpoints.getCurrentUser.initiate(undefined, { forceRefetch: true, subscribe: false }),
              );
              localStorage.removeItem(AddWorkspaceModal);
              setActiveModal(false);
            }
          });
      }
    }
  };

  return (
    <div
      className={`${styles.organizationContainer} ${showOnlyCreateForm ? styles.organizationContainerNoPadding : ''}`}
    >
      <p className={styles.organizationHeader}>
        {widthViewport > BreakpointMobile ? t('organization.title') : t('organization.titleMobile')}
      </p>
      <p className={styles.organizationDescription}>{t('organization.text')}</p>
      <div className={styles.organizationBody}>
        {widthViewport > BreakpointMobile && <ProgressSteps step={step} setSet={setStep} />}

        <div className={styles.organizationFormContainer}>
          <Form
            form={form}
            name="workspace"
            initialValues={{ name: '', phone: '', members: [{ email: '', role: 'viewer' }] }}
            autoComplete="off"
          >
            {step === 1 && (
              <>
                <div className={styles.organizationFormTitle}>{t('organization.step1.title')}</div>
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: t('forms.validation.required') }]}
                  validateTrigger="onBlur"
                >
                  <Input placeholder={t('organization.step1.placeholder')} size="large" />
                </Form.Item>
              </>
            )}
            {step === 2 && (
              <Form.List name="members">
                {(fields, { add, remove }) => (
                  <>
                    <div className={styles.organizationMembersHeader}>
                      <span className={styles.organizationMembersHeaderTitle}>{t('organization.step2.members')}</span>
                      {members?.length < 10 && (
                        <Button
                          onClick={() => add({ email: '', role: 'viewer' })}
                          type="secondary"
                          icon={<CroplerIcon name="add" />}
                        />
                      )}
                    </div>
                    <p className={styles.organizationMembersDescription}>{t('organization.step2.membersText')}</p>
                    {fields.map((field, index) => (
                      <div className={styles.organizationMember} key={field.key + index}>
                        <Form.Item
                          name={[field.name, 'email']}
                          rules={[{ type: 'email', message: t('forms.validation.emailInvalid') }]}
                          validateTrigger="onBlur"
                          className={styles.organizationMemberInfo}
                        >
                          <Input placeholder="Email" size="large" />
                        </Form.Item>
                        <Form.Item noStyle name={[field.name, 'role']}>
                          <Select
                            placeholder={t('labels.role')}
                            className={styles.organizationRole}
                            size="large"
                            suffixIcon={<CroplerIcon name="down" />}
                          >
                            {Roles.map((item) => {
                              return item.value !== 'owner' ? (
                                <Option key={item.value} value={item.value}>
                                  {item.name}
                                </Option>
                              ) : (
                                ''
                              );
                            })}
                          </Select>
                        </Form.Item>
                        {fields.length > 1 && widthViewport > BreakpointMobile && (
                          <Button onClick={() => remove(field.key)} size="large" type="default" icon={<CroplerIcon name="remove" />} />
                        )}
                        {fields.length > 1 && widthViewport < BreakpointMobileUp && (
                          <Button onClick={() => remove(field.key)} size="large" type="primary" icon={<CroplerIcon name="close" />} />
                        )}
                      </div>
                    ))}
                  </>
                )}
              </Form.List>
            )}
          </Form>
        </div>
      </div>
      <div className={styles.organizationFooter}>
        {widthViewport > BreakpointMobile && (
          <>
            {showOnlyCreateForm ? (
              <Button type="secondary" size={'large'} onClick={() => setActiveModal(false)}>
                Cancel
              </Button>
            ) : (
              <Button
                type="secondary"
                size={'large'}
                onClick={onBack}
                className={styles.backButton}
                icon={<CroplerIcon name="prev" />}
              >
                Back
              </Button>
            )}
          </>
        )}

        {step === 1 && (
          <Button onClick={onNext} type="primary" size={'large'} className={styles.nextButton} disabled={disabled}>
            {t('actions.next')}
          </Button>
        )}
        {step === 2 && (
          <Button onClick={onNext} type="primary" className={styles.nextButton} disabled={!submittable} size={'large'}>
            {showOnlyCreateForm ? t('actions.create') : t('actions.letsStarted')}
          </Button>
        )}
      </div>
    </div>
  );
};
